import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Backdrop,
    CircularProgress,
    Box,
    Autocomplete,
} from "@mui/material";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { getStockDiscounts, updateStockDiscounts } from "../../controllers/StockController";
import Message from "../../components/Message";
import AddProductsModal from "./components/AddProductsModal";
import { useSelector } from "react-redux";
import { getAllLocations } from "../../controllers/CatalogController";
import { flattenLocationStores } from "../../utils/locations";
import LiquidationSaleProductList from "./components/LiquidationSaleProductList";


export default function LiquidationSaleContainer() {
    const [searchParams] = useSearchParams();
    const storeId = searchParams.get("storeId");
    const hasSignedVoucher = Boolean(localStorage.getItem("signed-voucher"));
    const [manualStoreId, setManualStoreId] = useState(null);
    const effectiveStoreId = storeId || manualStoreId;
    const [stores, setStores] = useState([]);
    const [rows, setRows] = useState([]);
    const [queryInput, setQueryInput] = useState("");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const ruleEnums = useSelector((state) => state.globalData.enums);
    const stoppageCriteriaEnums = ruleEnums?.StoppageCriteria || [];
    const ruleTypeEnums = ruleEnums?.RuleType || [];
    const selectedStore = useMemo(
        () => stores.find((s) => String(s.storeId) === String(effectiveStoreId)),
        [stores, effectiveStoreId]
    );
    const [refreshedAt, setRefreshedAt] = useState(null);

    const [loading, setLoading] = useState(false);
    const [apiMessage, setApiMessage] = useState({
        open: false,
        type: "success",
        message: "",
    });

    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        fetchAllLocations();
    }, []);

    useEffect(() => {
        if (!effectiveStoreId) return;
        if (hasSignedVoucher) {
            localStorage.setItem("selected-store-id", effectiveStoreId);
        }
        getLiquidationSaleStockDiscounts(effectiveStoreId);
    }, [effectiveStoreId]);

    const fetchAllLocations = async () => {
        const res = await getAllLocations();
        const storeList = flattenLocationStores(res);
        setStores(storeList);
    }

    // Local-only: a manually picked store never touches redux or the URL.
    const handleStoreChange = (selected) => {
        setManualStoreId(selected?.storeId ?? null);
    };

    const getLiquidationSaleStockDiscounts = async (storeId) => {
        try {
            setLoading(true);
            const response = await getStockDiscounts({ storeId });
            const { content = [], totalElements = 0 } = response || {};
            setRows(content);
            setTotalElements(totalElements);
            setPage(0);
        } catch (err) {
            setRows([]);
            setTotalElements(0);
            setPage(0);

            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Something went wrong",
            });
        } finally {
            setLoading(false);
            setRefreshedAt(moment().format("lll"));

        }
    };

    const handleSearch = () => {
        setPage(0);
        setQuery(queryInput.trim());
    };


    const filteredRows = useMemo(() => {
        const searchText = query.toLowerCase().trim();

        if (!searchText) return rows;

        return rows.filter((row) => {
            const idValue = String(row.productItemId ?? row.id ?? "").toLowerCase();
            const nameValue = String(row.productItemName ?? row.name ?? "").toLowerCase();

            return idValue.includes(searchText) || nameValue.includes(searchText);
        });
    }, [rows, query]);

    const paginatedRows = useMemo(() => {
        const start = page * size;
        const end = start + size;
        return filteredRows.slice(start, end);
    }, [filteredRows, page, size]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredRows.length / size) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [filteredRows.length, page, size]);

    const handleChange = (index, field, value) => {
        const currentRow = paginatedRows[index];
        const actualIndex = rows.findIndex((row) => row === currentRow);

        if (actualIndex === -1) return;

        const updated = [...rows];
        const target = { ...updated[actualIndex] };

        if (!target._original) {
            target._original = { ...updated[actualIndex] };
        }

        target[field] = value;
        target.isUpdated = true;
        updated[actualIndex] = target;
        setRows(updated);
    };

    const onRemove = (row) => {
        const actualIndex = rows.findIndex((r) => r === row);

        if (actualIndex === -1) return;
        const updated = [...rows];
        if (row.isAdded) {

            updated.splice(actualIndex, 1);
            setRows(updated);
        } else {
            const target = { ...updated[actualIndex] };
            if (!target._original) {
                target._original = { ...updated[actualIndex] };
            }
            target.permanentlyDeactivated = true;
            updated[actualIndex] = target;
            setRows(updated);
        }
    }

    const onRevert = (index) => {
        const currentRow = paginatedRows[index];
        const actualIndex = rows.findIndex((row) => row === currentRow);

        if (actualIndex === -1) return;

        const updated = [...rows];
        const target = { ...updated[actualIndex] };
        if (!target?._original) return;

        const reverted = { ...target._original };
        delete reverted._original;
        reverted.isUpdated = false;
        reverted.permanentlyDeactivated = false;

        updated[actualIndex] = reverted;
        setRows(updated);
    }

    const validate = () => {

        const enabledRows = rows.filter((row) => Boolean(row.enabled));
        const updatedRows = enabledRows.filter((row) => row.isUpdated || row.isAdded);
        const addedRows = enabledRows.filter((row) => row.isAdded);

        const invalidLiquidationPriceRow = updatedRows.find((row) => {
            const price = row?.applicableSellingPrice;
            return price === null || price === undefined || String(price).trim() === "";
        });

        if (invalidLiquidationPriceRow) {
            setApiMessage({
                open: true,
                type: "error",
                message: "Cannot update: liquidation price is missing for one or more enabled rows",
            });
            return false;
        }

        const invalidQtyRow = updatedRows.find((row) => {
            const qty = Number(row.qty);
            return Number.isNaN(qty) || qty <= 0;
        });

        if (invalidQtyRow) {
            setApiMessage({
                open: true,
                type: "error",
                message: "Enabled rows must have quantity greater than 0",
            });
            return false;
        }

        const invalidTimeRow = addedRows.find((row) => {
            const datePart = row.startDate ? moment(row.startDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
            if (!row.startTime) return true;

            const startDateTime = moment(`${datePart} ${row.startTime}`, "YYYY-MM-DD HH:mm:ss", true);
            return !startDateTime.isValid() || !startDateTime.isAfter(moment());
        });

        if (invalidTimeRow) {
            setApiMessage({
                open: true,
                type: "error",
                message: "Added Products must have a valid future start time",
            });
            return false;
        }
        return true;
    }

    const handleUpdate = async () => {
        if (!validate()) return;
        try {
            setLoading(true);
            const toUpdate = rows.filter(row => row.isUpdated || row.isAdded || row.permanentlyDeactivated).map((row) => ({
                id: row.id,
                productItemId: row.productItemId,
                qty: row.qty,
                startDate: row.startDate,
                startTime: row.startTime,
                stoppageCriteria: row.stoppageCriteria,
                enabled: row.enabled,
                storeId: row.storeId,
                permanentlyDeactivated: row.permanentlyDeactivated
            }));
            if (toUpdate.length === 0) {
                setApiMessage({
                    open: true,
                    type: "error",
                    message: "No changes to update",
                });
                return;
            }
            await updateStockDiscounts(toUpdate);
            setApiMessage({
                open: true,
                type: "success",
                message: "Stock discounts updated successfully",
            });
            getLiquidationSaleStockDiscounts(effectiveStoreId);
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Failed to update stock discounts",
            });
        } finally {
            setLoading(false);
        }
    }

    const handleAddProducts = async () => {
        setModalOpen(true);
    };

    const handleConfirmAdd = (newProducts) => {
        const getKey = (item) => String(item?.productItemId ?? item?.id ?? "");

        const incomingById = new Map(
            newProducts.map((p) => [getKey(p), p])
        );

        const updatedExistingRows = rows.map((row) => {
            if (row.permanentlyDeactivated) return row;
            const incoming = incomingById.get(getKey(row));
            if (!incoming) return row;

            return {
                ...row,
                applicableSellingPrice:
                    incoming.liquidationPrice ??
                    incoming.applicableSellingPrice ??
                    row.applicableSellingPrice,
                lastStockRefreshTimestamp:
                    incoming.lastUpdatedTime ?? row.lastStockRefreshTimestamp,
            };
        });

        const existingIds = new Set(rows.map((row) => getKey(row)));
        const productsToAdd = newProducts.filter(
            (product) => !existingIds.has(getKey(product))
        );

        setRows([...updatedExistingRows, ...productsToAdd]);
        setModalOpen(false);

        setApiMessage({
            open: true,
            type: "success",
            message: `Added product(s)`,
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 999,
                    color: "#fff",
                    backdropFilter: "blur(3px)",
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Card sx={{ p: 1, mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                        <Typography variant="h6" mb={1}>
                            Liquidation Sale Products
                        </Typography>
                        <Typography variant="body2" mb={1} color="primary">
                            Refreshed At:{refreshedAt ? ` ${refreshedAt}` : " -"}
                        </Typography>
                    </Box>


                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 3 }}>
                            <Typography
                                variant="caption"
                                sx={{ display: "block", mb: 0.5, color: "text.secondary", fontWeight: 500 }}
                            >
                                Selected Store
                            </Typography>
                            {!hasSignedVoucher ? (
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={stores}
                                    value={selectedStore || null}
                                    isOptionEqualToValue={(option, selected) => String(option.storeId) === String(selected.storeId)}
                                    getOptionLabel={(option) => `${option?.storeId} - ${option?.storeName || ""}`}
                                    onChange={(_, selected) => handleStoreChange(selected)}
                                    renderInput={(params) => <TextField {...params} placeholder="Select a store" />}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        height: 40,
                                        px: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        border: "1px solid",
                                        borderColor: "grey.400",
                                        borderRadius: 1,
                                        backgroundColor: "#f5f5f5",
                                        color: "text.primary",
                                        userSelect: "none",
                                        cursor: "default",
                                    }}
                                >
                                    {`${effectiveStoreId} - ${selectedStore?.storeName || ""}`}
                                </Box>
                            )}
                        </Grid>

                        <Grid size={{ xs: 3 }}>
                            <TextField
                                label="Search Products"
                                fullWidth
                                size="small"
                                value={queryInput}
                                onChange={(e) => setQueryInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </Grid>

                        <Grid >
                            <Button variant="contained" onClick={handleSearch} disabled={loading || !effectiveStoreId}>
                                Search Products
                            </Button>
                        </Grid>
                        <Grid >
                            <Button variant="outlined" onClick={handleAddProducts} disabled={loading || !effectiveStoreId}>
                                Add Products
                            </Button>
                        </Grid>

                        <Grid sx={{ marginLeft: "auto" }}>
                            <Button variant="contained" onClick={handleUpdate}
                                disabled={
                                    loading || !rows.some(row => (row.isUpdated || row.isAdded || row.permanentlyDeactivated))
                                }>Update Changes</Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <LiquidationSaleProductList
                rows={paginatedRows}
                onChange={handleChange}
                totalElements={filteredRows.length}
                page={page}
                size={size}
                setPage={setPage}
                setSize={setSize}
                stoppageCriteriaEnums={stoppageCriteriaEnums}
                onRemove={onRemove}
                onRevert={onRevert}
            />
            <AddProductsModal
                storeId={effectiveStoreId}
                selectedProducts={[]}
                loadingProducts={false}
                handleConfirmAdd={handleConfirmAdd}
                modalOpen={modalOpen}
                handleCloseModal={() => setModalOpen(false)}
                setApiMessage={setApiMessage}
                stoppageCriteriaEnums={stoppageCriteriaEnums}
                ruleTypeEnums={ruleTypeEnums}
            />
            <Message
                open={apiMessage.open}
                type={apiMessage.type}
                message={apiMessage.message}
                onClose={() => setApiMessage((p) => ({ ...p, open: false }))}
            />
        </LocalizationProvider>
    );
}